import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { IHttpService } from 'angular'
import { RootState } from '../../../common/redux/root.reducer'
import { URLS } from '../../../common/utilities/constants/constants.module'
import { DataBlock, DataGroupOverviewType, DataGroupSideBar, DisplayBlockSection } from '../types/types'
import { MydataBlocks, MyDataGroups, MyDataGroupsOverview, OrganisationaldataBlocks, OrganisationalDataGroups } from '../data'
import { RelationDetailed } from '../../overview/types/RelationDetailed'
import { parseHttpError } from '../../../common/utilities/errorHandling'
import { Org } from '../../organisations/OrganisationAccounts/types'

export interface AnalyticsState {
  graphQlData: any | null
  loadingGraphQL: boolean
  errorGraphQL: string | null

  dataGroupDetailed: DataGroupSideBar | null
  loadingDataGroupDetailed: boolean
  errorDataGroupDetailed: string | null

  dataBlock: DataBlock | null
  loadingDataBlock: boolean
  errorDataBlock: string | null

  organisations: RelationDetailed[]
  loadingOrganisations: boolean
  errorOrganisations: string | null

  dataGroupOverview: DataGroupOverviewType[]
  loadingDataGroupOverview: boolean
  errorDataGroupOverview: string | null
}

export interface GraphQLRequest {
  filters: Record<string, any>
  displayBlocks: DisplayBlockSection[]
}

interface GraphQLResponse {
  data: { [key: string]: any }
}

export const buildGraphQLQuery = (
  path: string[],
  selectedFields: any[],
  filters: Record<string, any>,
  key: number
): {query: string, operationName: string, key: number} => {
  const queryName = `MyQuery${key}`
  const fieldsStr = selectedFields.map(f => f.key).join(' ')

  function buildNestedFields (index: number): string {
    const key = path[index]

    if (index === path.length - 1) {
      const filterArgs = Object.entries(filters)
        .filter(([_, val]) => val !== '' && val !== undefined && val !== null)
        .map(([k, val]) => {
          if (typeof val === 'string') {
            return `${k}: "${val}"`
          }
          return `${k}: ${val}`
        })
        .join(', ')

      const argsStr = filterArgs ? `(${filterArgs})` : ''

      return `${key}${argsStr} { ${fieldsStr} }`
    } else {
      return `${key} { ${buildNestedFields(index + 1)} }`
    }
  }

  const nestedFields = buildNestedFields(0)
  return { query: `query ${queryName} { ${nestedFields} }`, operationName: queryName, key }
}

export const getGraphQLs = createAsyncThunk<
any,
GraphQLRequest,
{ extra: { $http: IHttpService } }
>(
  'graphql/executeMultiple',
  async (body, thunkApi) => {
    const { $http } = thunkApi.extra
    const url = `${URLS.admin.graphql}`

    const requests = body.displayBlocks.map((db) => buildGraphQLQuery(db.path, db.selectedFields, body.filters, db.id))

    try {
      const results: GraphQLResponse[] = await Promise.all(
        requests.map(async (req) => {
          const response = await $http.post<GraphQLResponse>(url, {
            operationName: req.operationName,
            query: req.query
          })

          const path = body.displayBlocks.find(db => db.id === req.key)?.path
          const data = response.data
          const parsedData = path.reduce((acc: any, key: any) => (acc?.[key] !== undefined ? acc[key] : undefined), data?.data)
          return {
            key: req.key,
            data: parsedData
          }
        })
      )
      return results
    } catch (err) {
      return thunkApi.rejectWithValue(err)
    }
  }
)

export const fetchDataGroupDetailed = createAsyncThunk<
DataGroupSideBar | null,
{ dataGroupId?: number, relationId?: number }
>(
  'dataGroup',
  async (body, thunkApi) => {
    return await new Promise<DataGroupSideBar | null>((resolve) => {
      setTimeout(() => {
        // TODO get live data
        if (body.relationId) {
          // Will be resolved based on relationId
          resolve(OrganisationalDataGroups || null)
        } else {
          // Will be resolved based on data group id
          resolve(MyDataGroups || null)
        }
      }, 500) // fake 500ms delay
    })
  }
)

export const fetchDataGroupOverview = createAsyncThunk<
DataGroupOverviewType[],
undefined
>(
  'dataGroupOverview',
  async (_, thunkApi) => {
    return await new Promise<DataGroupOverviewType[]>((resolve) => {
      setTimeout(() => {
        // TODO get live data
        resolve(MyDataGroupsOverview)
      }, 500) // fake 500ms delay
    })
  }
)

export const fetchDataBlock = createAsyncThunk<
DataBlock | null,
{dataBlockId: number, dataGroupId?: number, relationId?: number }
>(
  'dataBlock',
  async (body, thunkApi) => {
    return await new Promise<DataBlock | null>((resolve) => {
      setTimeout(() => {
        // TODO get live data
        if (body.dataBlockId && body.dataGroupId) {
          resolve(MydataBlocks || null)
        } else if (body.relationId) {
          resolve(OrganisationaldataBlocks || null)
        } else {
          resolve(null)
        }
      }, 500) // fake 500ms delay
    })
  }
)

export const fetchOrganisations = createAsyncThunk<
RelationDetailed[],
undefined,
{ extra: { $http: IHttpService } }
>('fetchOrg', async (_, thunkApi) => {
  const url = `${URLS.admin.companies}?type=Customer&type=Depot`
  const { data: relations } =
    await thunkApi.extra.$http
      .get<Org[]>(url)
      .catch(parseHttpError)
  return relations.map((r) => ({ id: r.relationId, name: r.name, teamleaderId: r.teamleaderId }))
})

const initialState: AnalyticsState = {
  graphQlData: null,
  loadingGraphQL: false,
  loadingDataGroupDetailed: false,
  loadingDataBlock: false,
  errorGraphQL: null,
  errorDataGroupDetailed: null,
  errorDataBlock: null,
  dataGroupDetailed: null,
  dataBlock: null,
  organisations: [],
  loadingOrganisations: false,
  errorOrganisations: null,
  dataGroupOverview: [],
  errorDataGroupOverview: null,
  loadingDataGroupOverview: false
}

const dataGroupSlice = createSlice({
  name: 'dataGroup',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getGraphQLs.pending, (state) => {
        state.loadingGraphQL = true
        state.errorGraphQL = null
      })
      .addCase(getGraphQLs.fulfilled, (state, action) => {
        state.loadingGraphQL = false
        state.graphQlData = action.payload
      })
      .addCase(getGraphQLs.rejected, (state, action) => {
        state.loadingGraphQL = false
        state.errorGraphQL = action.error.message || 'Failed to load'
      })
      .addCase(fetchDataGroupDetailed.pending, (state) => {
        state.loadingDataGroupDetailed = true
        state.errorDataGroupDetailed = null
        state.dataGroupDetailed = null
      })
      .addCase(fetchDataGroupDetailed.fulfilled, (state, action) => {
        state.loadingDataGroupDetailed = false
        state.dataGroupDetailed = action.payload
      })
      .addCase(fetchDataGroupDetailed.rejected, (state, action) => {
        state.loadingDataGroupDetailed = false
        state.errorDataGroupDetailed = action.error.message || 'Failed to load data group'
      })
      .addCase(fetchDataBlock.pending, (state) => {
        state.loadingDataBlock = true
        state.errorDataBlock = null
        state.dataBlock = null
      })
      .addCase(fetchDataBlock.fulfilled, (state, action) => {
        state.loadingDataBlock = false
        state.dataBlock = action.payload
      })
      .addCase(fetchDataBlock.rejected, (state, action) => {
        state.loadingDataBlock = false
        state.errorDataBlock = action.error.message || 'Failed to load data block'
      })
      .addCase(fetchOrganisations.pending, (state) => {
        state.loadingOrganisations = true
        state.errorOrganisations = null
        state.organisations = []
      })
      .addCase(fetchOrganisations.fulfilled, (state, action) => {
        state.loadingOrganisations = false
        state.organisations = action.payload
      })
      .addCase(fetchOrganisations.rejected, (state, action) => {
        state.loadingOrganisations = false
        state.errorOrganisations = action.error.message || 'Failed to load data block'
      })
      .addCase(fetchDataGroupOverview.pending, (state) => {
        state.loadingDataGroupOverview = true
        state.errorDataGroupOverview = null
        state.dataGroupOverview = []
      })
      .addCase(fetchDataGroupOverview.fulfilled, (state, action) => {
        state.loadingDataGroupOverview = false
        state.dataGroupOverview = action.payload
      })
      .addCase(fetchDataGroupOverview.rejected, (state, action) => {
        state.loadingDataGroupOverview = false
        state.errorDataGroupOverview = action.error.message || 'Failed to load data group'
      })
  }
})

const { reducer } = dataGroupSlice

export const dataGroupSelector = (state: RootState): AnalyticsState => state.dataGroup

export const dataGroupReducer = reducer
